import { computed, Ref, ref } from "vue";
import {
  CertificationVM,
  CertificationHeaderVM,
  CertificationForm,
  CertificationFormOptions,
  CertificationStatusIds,
} from "@/types/certifications";
import * as yup from "yup";
import { ProductIds } from "@/types/types";
import {
  ISO_8601_FORMAT,
  getCertifiedAmountValidation,
} from "@/composables/validationUtils";
import {
  DisbursementStatusIds,
  DisbursementFormOptions,
  DisbursementVM,
} from "@/types/disbursements";

export interface ValidationData {
  certification: Ref<CertificationVM>;
  mode: Ref<"view" | "add" | "edit">;
  productTypeId: Ref<number>;
  formOptions: Ref<CertificationFormOptions>;
  formData: Ref<CertificationForm>;
  createdDate: Ref<string>;
  disbursementFormOptions?: Ref<DisbursementFormOptions>;
}

function filterFields(data: ValidationData, fields: any[]) {
  return fields.filter((field) => {
    if (data.mode.value === "add") {
      return field.showOnAdd !== false;
    } else if (data.mode.value === "edit") {
      return field.showOnEdit !== false;
    }

    return [];
  });
}

export function useCertificationForm(data: ValidationData) {
  const disbursements = ref<DisbursementVM[]>([]);
  const dateSchema = yup.date().required("Date is required");

  const editSchema = computed(() => {
    return yup.object().shape({
      cluid: yup.string().required("CLUID is required"),
      requestedAmount: yup
        .number()
        .test(
          "has-requested-amount",
          "Requested Amount is required",
          function (value, context) {
            if (data.productTypeId.value === ProductIds.LOC) {
              return (
                value !== null && value !== undefined && value !== Number(0)
              );
            }

            return true;
          }
        ),
      sequenceNumber: yup.string().notRequired(),
      certifiedAmount: getCertifiedAmountValidation(
        () => Number(data.formData.value?.requestedAmount),
        () => data.formData.value?.requestedAmountDisplay || ""
      )
        .label("Cert. Amount")
        .test("is-valid", "Cert. Amount is required", (value, testContext) => {
          const hasValue = value !== null && value !== undefined;

          const hasCertificationReceiveDate =
            data.formData.value.certificationReceiveDate === undefined ||
            data.formData.value.certificationReceiveDate === null;

          const isCertified =
            data.formData.value.certificationStatusTypeId &&
            data.formData.value.certificationStatusTypeId ===
              CertificationStatusIds.Certified;

          if (isCertified || hasCertificationReceiveDate) {
            return hasValue === true;
          } else {
            return true;
          }
        }),
      certificationMethodTypeId: yup.number(),
      certificationStatusTypeId: yup.number(),
      certificationSendDate: yup.string().notRequired(),
      certificationReceiveDate: yup.string().notRequired(),
      enrollmentStatusTypeId: yup
        .number()
        .required("Enrollment Status is required"),
      schoolLevelTypeId: yup.number().label("Grade Level").required(),
      anticipatedGraduationDate: yup.date(),
      enrollmentTermId: yup.number().label("Requested Term"),
      loanPeriodStartDate: yup.date().when(["loanPeriodEndDate"], {
        is: (loanPeriodEndDate: Date) => {
          const hasLoanPeriodEndDate =
            loanPeriodEndDate !== null && loanPeriodEndDate !== undefined;

          if (hasLoanPeriodEndDate) {
            return true;
          }

          return false;
        },
        then: (schema) =>
          yup
            .date()
            .transform((curr, orig) => (orig === "" ? null : curr))
            .test(
              "dates-test",
              "Loan Period Start Date should be before Loan Period End Date",
              (value, context) => {
                if (context.parent.loanPeriodEndDate && value) {
                  const startDate = new Date(value).setHours(0, 0, 0, 0);

                  const endDate = new Date(
                    context.parent.loanPeriodEndDate
                  ).setHours(0, 0, 0, 0);
                  return endDate > startDate;
                } else if (context.parent.loanPeriodEndDate && !value) {
                  context.createError({
                    message: "Loan Period Start Date is required",
                  });
                } else {
                  return true;
                }
              }
            )
            .required("Cert. Receive Date is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      loanPeriodEndDate: yup
        .date()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .test(
          "dates-test",
          "Loan Period End Date should be after Loan Period Start Date",
          (value, context) => {
            if (context.parent.loanPeriodStartDate && value) {
              const startDate = new Date(
                context.parent.loanPeriodStartDate
              ).setHours(0, 0, 0, 0);

              const endDate = new Date(value).setHours(0, 0, 0, 0);
              return endDate > startDate;
            } else {
              return true;
            }
          }
        ),
    });
  });

  const formFieldGroup1 = computed(() => {
    const fields = [
      {
        name: "cluid",
        label: "CLUID",
        type: "text",
        width: "col-6",
        value: data.formData.value?.cluid,
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        readonly: false,
        category: "certification",
      },
      {
        name: "requestedAmount",
        label: "Requested Amount",
        type: "money",
        width: "col-6",
        value:
          data.mode.value === "edit" &&
          data.productTypeId.value === ProductIds.LOC
            ? data.formData.value?.requestedAmount
            : data.formData.value?.requestedAmountDisplay,
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        readonly: true,
        category: "Draw",
      },
      {
        name: "sequenceNumber",
        label: "Sequence",
        type: "text",
        width: "col-6",
        value: data.formData.value?.sequenceNumber,
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        readonly: true,
        category: "certification",
      },
      {
        name: "originalCertifiedAmount",
        label: "Original Cert. Amount",
        type: "money",
        width: "col-6",
        value: data.formData.value?.originalCertifiedAmountDisplay,          
        showOnAdd: "text",
        showOnEdit: true,
        disabledOnEdit: true,
        readonly: true,
        title: "Original Certified Amount",
        category: "certification",
      },
      {
        name: "certificationMethodTypeId",
        label: "Cert. Method",
        type: "select",
        width: "col-6",
        value:
          data.mode.value === "edit"
            ? data.formData.value?.certificationMethodTypeId
            : data.formData.value?.certificationMethodTypeName,
        options: data.formOptions.value?.certificationMethods,
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        readonly: false,
        title: "Certification Method",
        category: "certification",
      },
      {
        name: "certifiedAmount",
        label: "Cert. Amount",
        type: "money",
        width: "col-6",
        value:
          data.mode.value === "edit"
            ? data.formData.value?.certifiedAmount
            : data.formData.value?.certifiedAmountDisplay,
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        readonly: false,
        title: "Certified Amount",
        category: "certification",
      },
    ];

    return filterFields(data, fields);
  });

  const formFieldGroup2 = computed(() => {
    const fields = [
      {
        name: "certificationStatusTypeId",
        label: "Cert. Status",
        type: "select",
        width: "col-12",
        value:
          data.mode.value === "edit"
            ? data.formData.value?.certificationStatusTypeId
            : data.formData.value?.certificationStatusTypeName,
        options: data.formOptions.value?.certificationStatuses,
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        readonly: false,
        title: "Certification Status",
        category: "certification",
      },
      {
        name: "certificationSendDate",
        label: "Cert. Send Date",
        type: "date",
        width: "col-12",
        value: data.formData.value?.certificationSendDateDisplay,
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        readonly: true,
        title: "Certification Send Date",
        required: false,
        category: "certification",
      },
      {
        name: "certificationReceiveDate",
        label: "Cert. Receive Date",
        type: "date",
        width: "col-12",
        value: data.formData.value?.certificationReceiveDateDisplay,
        options: null,
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        readonly: true,
        title: "Certification Receive Date",
        category: "certification",
      },
    ];

    return filterFields(data, fields);
  });

  const formFieldGroup3 = computed(() => {
    const fields = [
      {
        name: "enrollmentStatusTypeId",
        label: "Enrollment Status",
        type: "select",
        width: "col-12",
        value:
          data.mode.value === "edit"
            ? data.formData.value?.enrollmentStatusTypeId
            : data.formData.value?.enrollmentStatusTypeName,
        options: data.formOptions.value?.enrollmentStatuses,
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        readonly: false,
        category: "draw",
      },
      {
        name: "schoolLevelTypeId",
        label: "Grade Level",
        type: "select",
        width: "col-12",
        value:
          data.mode.value === "edit"
            ? data.formData.value?.schoolLevelTypeId
            : data.formData.value?.schoolLevelDisplay,
        options: data.formOptions.value?.schoolLevels,
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        readonly: false,
        category: "draw",
      },
      {
        name: "anticipatedGraduationDate",
        label: "Anticipated Grad. Date",
        type: "date",
        width: "col-12",
        value:
          data.mode.value === "edit"
            ? data.formData.value?.anticipatedGraduationDate
            : data.formData.value?.anticipatedGraduationDateDisplay,
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        readonly: false,
        title: "Anticipated Graduation Date",
        category: "draw",
      },
    ];

    return filterFields(data, fields);
  });

  const formFieldGroup4 = computed(() => {
    const fields = [
      {
        name: "enrollmentTermId",
        label: "Requested Term",
        type: "select",
        width: "col-12",
        value:
          data.mode.value === "edit" &&
          data.productTypeId.value === ProductIds.LOC
            ? data.formData.value?.enrollmentTermId
            : data.formData.value?.enrollmentTermDisplay,
        options: data.formOptions.value?.enrollmentTerms,
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        readonly: data.productTypeId.value === ProductIds.PEL, // editable for LOC, but not PEL
        category: "certification",
      },
      {
        name: "loanPeriodStartDate",
        label: "Loan Period Start Date",
        type: "date",
        width: "col-12",
        value:
          data.mode.value === "edit"
            ? data.formData.value?.loanPeriodStartDate
            : data.formData.value?.loanPeriodStartDateDisplay,
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: true,
        readonly: false,
        category: "certification",
      },
      {
        name: "loanPeriodEndDate",
        label: "Loan Period End Date",
        type: "date",
        width: "col-12",
        value:
          data.mode.value === "edit"
            ? data.formData.value?.loanPeriodEndDate
            : data.formData.value?.loanPeriodEndDateDisplay,
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: true,
        readonly: false,
        category: "certification",
      },
    ];

    return filterFields(data, fields);
  });

  const formFields = computed(() => {
    return formFieldGroup1.value.concat(
      formFieldGroup2.value,
      formFieldGroup3.value,
      formFieldGroup4.value
    );
  });

  const validationSchema = computed(() => {
    switch (data.mode.value) {
      case "edit":
        return editSchema.value;
      default:
        return yup.object({});
    }
  });

  return {
    editSchema,
    validationSchema,
    formFields,
    formFieldGroup1,
    formFieldGroup2,
    formFieldGroup3,
    formFieldGroup4,
  };
}
